import React, { useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import FadeInSection from './FadeInSection';
import '../styles/Buyers.scss';

const STEPS = [
  {
    number: '01',
    title: 'Get Pre-Approved',
    duration: '1–2 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="3" y="2" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M7 7h8M7 11h8M7 15h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M14 13l1.5 1.5L18 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    description:
      'Before you start touring homes, get a mortgage pre-approval from a lender. In the Bay Area, most sellers require proof of pre-approval with any offer. This step also clarifies your true budget.',
    tips: [
      'Compare at least 2–3 lenders for rate and fees',
      'Avoid opening new credit lines during this period',
      'Ask about loan types: conventional, jumbo, FHA',
    ],
  },
  {
    number: '02',
    title: 'Define Your Search',
    duration: '1–4 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M15 15l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'Narrow down neighborhoods, property types, and must-haves. Lily will set up a custom MLS search so you receive new listings the moment they hit the market.',
    tips: [
      'Prioritize commute, schools, and lifestyle fit',
      'Consider off-market and coming-soon listings',
      'Visit neighborhoods at different times of day',
    ],
  },
  {
    number: '03',
    title: 'Tour Homes',
    duration: '2–8 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M3 10L11 3l8 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 8.5V19a1 1 0 001 1h4v-5h2v5h4a1 1 0 001-1V8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    description:
      'Actively tour properties — both open houses and private showings. Bay Area inventory moves fast; being ready to act quickly is critical.',
    tips: [
      'Review disclosure packages before making an offer',
      'Take notes and photos at each home',
      'Ask about permit history and any HOA details',
    ],
  },
  {
    number: '04',
    title: 'Make an Offer',
    duration: '1–3 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M3 17l3-3 9.5-9.5a1.5 1.5 0 012.1 2.1L8.1 16 5 17z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'When you find the right home, Lily will analyze comparable sales and guide you on offer price, contingencies, and terms to make your offer competitive in the current market.',
    tips: [
      'Review the disclosure package thoroughly first',
      'Understand what contingencies to include or waive',
      'An escalation clause can help in competitive situations',
    ],
  },
  {
    number: '05',
    title: 'Open Escrow',
    duration: 'Day 1 of escrow',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="4" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M8 10V7a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="11" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
    ),
    description:
      'Once your offer is accepted, a neutral third-party escrow company holds all funds and documents. Your earnest money deposit (typically 3%) is due within 3 business days.',
    tips: [
      'Earnest money is typically 1–3% of the purchase price',
      'Escrow coordinates with lender, title, and both agents',
      'Keep all financial records readily accessible',
    ],
  },
  {
    number: '06',
    title: 'Inspections & Due Diligence',
    duration: '7–17 days',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="5" y="2" width="12" height="18" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M9 2v2h4V2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M8 9h6M8 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'Even in as-is sales, you can conduct inspections for informational purposes. This includes general home inspection, sewer scope, roof, and any specialist inspections based on what was found.',
    tips: [
      'In SF, many sellers provide inspection reports upfront',
      'A sewer lateral inspection is highly recommended',
      'Review all transfer disclosure statements carefully',
    ],
  },
  {
    number: '07',
    title: 'Loan Finalization',
    duration: '2–3 weeks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="2" y="7" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M6 7V5a5 5 0 0110 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M6 13h10M6 16h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    description:
      'Your lender will order an appraisal and finalize underwriting. Avoid any major financial changes — new jobs, large purchases, or new credit — during this period.',
    tips: [
      'Respond to lender document requests within 24 hours',
      'Lock your interest rate if you haven\'t already',
      'Review the Closing Disclosure 3 days before closing',
    ],
  },
  {
    number: '08',
    title: 'Close Escrow',
    duration: 'Closing day',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="8" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M11.5 10H19v3h-2v3h-3v-3h-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8" cy="10" r="1" fill="currentColor"/>
      </svg>
    ),
    description:
      'You\'ll do a final walkthrough, sign closing documents, and wire your remaining funds. Once the deed records with the county, the keys are yours.',
    tips: [
      'Wire funds at least 1 business day before closing',
      'Bring a government-issued photo ID to signing',
      'Recording typically happens by end of business day',
    ],
  },
];

const STATS = [
  { value: '$1.35M', label: 'Bay Area median home price' },
  { value: '21–30', label: 'Days in escrow' },
  { value: '3%', label: 'Typical earnest money' },
  { value: '1–3%', label: 'Down payment floor (FHA)' },
];

export default function Buyers() {
  const [activeStep, setActiveStep] = useState(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="buyers-page">
      <Helmet>
        <title>Bay Area Buyer's Guide | Lily Lai Real Estate</title>
        <meta name="description" content="A step-by-step guide to buying a home in the Bay Area. From pre-approval to closing day, Lily Lai walks you through all 8 steps of the process." />
        <link rel="canonical" href="https://www.lilylaisf.com/buyers" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.lilylaisf.com/buyers" />
        <meta property="og:title" content="Bay Area Buyer's Guide — 8 Steps to Homeownership | Lily Lai Real Estate" />
        <meta property="og:description" content="From pre-approval to closing day — everything you need to know about buying a home in the Bay Area, step by step." />
        <meta property="og:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bay Area Buyer's Guide — 8 Steps to Homeownership | Lily Lai Real Estate" />
        <meta name="twitter:description" content="From pre-approval to closing day — everything you need to know about buying a home in the Bay Area, step by step." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.lilylaisf.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Bay Area Buyer's Guide", "item": "https://www.lilylaisf.com/buyers" }
                ]
              },
              {
                "@type": "Article",
                "mainEntityOfPage": "https://www.lilylaisf.com/buyers",
                "headline": "The Bay Area Buyer's Guide",
                "description": "A complete step-by-step guide to buying a home in the Bay Area, from getting pre-approved to closing escrow.",
                "image": "https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr",
                "articleSection": "Buyer Resources",
                "author": {
                  "@type": "Person",
                  "name": "Lily Lai",
                  "url": "https://www.lilylaisf.com/about"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Lily Lai Real Estate",
                  "url": "https://www.lilylaisf.com/"
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      {/* ── Header ── */}
      <div className="buyers-header">
        <FadeInSection>
          <p className="buyers-eyebrow">Buyer Resources</p>
          <h1 className="buyers-title">The Bay Area Buyer's Guide</h1>
          <p className="buyers-subtitle">
            Buying a home in the Bay Area is exciting — and complex. Here's exactly what to expect, step by step.
          </p>
          <p className="buyers-meta">8 steps · typically 2–6 months start to close</p>
        </FadeInSection>
      </div>

      {/* ── Stats strip ── */}
      <FadeInSection>
        <div className="buyers-stats">
          {STATS.map((s) => (
            <div key={s.label} className="buyers-stat">
              <span className="buyers-stat-value">{s.value}</span>
              <span className="buyers-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </FadeInSection>

      {/* ── Step tracker ── */}
      <FadeInSection>
        <div className="buyers-tracker">
          {STEPS.map((step, i) => (
            <button
              key={step.number}
              className={`buyers-tracker-step${activeStep === i ? ' active' : ''}`}
              onClick={() => {
                setActiveStep(activeStep === i ? null : i);
                if (typeof window.gtag === 'function') window.gtag('event', 'buyers_step_expand', { step_name: step.title });
              }}
              aria-label={step.title}
              title={step.title}
            >
              <span className="buyers-tracker-num">{step.number}</span>
              <span className="buyers-tracker-label">{step.title}</span>
            </button>
          ))}
        </div>
      </FadeInSection>

      {/* ── Timeline ── */}
      <div className="buyers-timeline-wrap">
        <FadeInSection>
          <div className="buyers-timeline">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.number}
                className={`buyers-step${activeStep === i ? ' buyers-step--open' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              >
                <button
                  className="buyers-step-header"
                  onClick={() => {
                    setActiveStep(activeStep === i ? null : i);
                    if (typeof window.gtag === 'function') window.gtag('event', 'buyers_step_expand', { step_name: step.title });
                  }}
                  aria-expanded={activeStep === i}
                >
                  <div className="buyers-step-number">{step.number}</div>
                  <div className="buyers-step-icon">{step.icon}</div>
                  <div className="buyers-step-meta">
                    <span className="buyers-step-title">{step.title}</span>
                    <span className="buyers-step-duration">{step.duration}</span>
                  </div>
                  <div className={`buyers-step-chevron${activeStep === i ? ' open' : ''}`} aria-hidden="true">
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                      <path d="M1 1l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {activeStep === i && (
                    <motion.div
                      className="buyers-step-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="buyers-step-body-inner">
                        <p className="buyers-step-desc">{step.description}</p>
                        <ul className="buyers-step-tips">
                          {step.tips.map((tip, j) => (
                            <li key={j}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </FadeInSection>
      </div>

      {/* ── CTA strip ── */}
      <FadeInSection>
        <div className="buyers-cta-strip">
          <div className="buyers-cta-content">
            <p className="buyers-cta-heading">Ready to start your search?</p>
            <p className="buyers-cta-sub">Lily will guide you every step of the way.</p>
          </div>
          <div className="buyers-cta-actions">
            <a
              href="/contact"
              className="buyers-cta-btn buyers-cta-btn--primary"
              onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'buyers_cta_click', { button_label: 'Get in Touch' }); }}
            >Get in Touch</a>
            <a
              href="/mortgage-calculator"
              className="buyers-cta-btn buyers-cta-btn--secondary"
              onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'buyers_cta_click', { button_label: 'Mortgage Calculator' }); }}
            >Mortgage Calculator</a>
          </div>
        </div>
      </FadeInSection>
    </div>
  );
}
